import { Component, OnDestroy, OnInit } from '@angular/core';
import { Role } from '../shared/models/role';
import { UserRoleService } from '../services/user-role.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { ProjectColumnAccessModel } from '../shared/models/project-column-access-model';
import { AccessPermission } from '../shared/models/access-permission';
import { AccessSettingActionComponent } from './access-setting-action.component';
import { ToastService } from '../shared/toast/toast.service';
import { MenuItem } from 'primeng/api';
import { SessionService } from '../services/session.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { SubSink } from 'subsink';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'pps-project-column-access-setting',
  templateUrl: './project-column-access-setting.component.html',
  styleUrls: ['./project-column-access-setting.component.css']
})
export class ProjectColumnAccessSettingComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
 
  spinnerName: string = "spinner";
  spinnerText: string = "Loading data ...";
   
  smallScreen: boolean = false;
  
  items: MenuItem[] = [
    {label: "Admin", icon: "pi-fw fas fa-user-lock"},
    {label: "Project Column Access Settings", icon: "pi-fw fas fa-clipboard-list"}
  ];

  loading: boolean = false;

  roles: Role[] = [];
  roleGridHeaders: any = [];

  projectColumns: ProjectColumnAccessModel[] = [];
  projectColumnGridHeaders: any = [];

  context:any = {};
  selectedRole: any = null;

  private roleGridApi: any;
  projectColumnGridApi: any;

  constructor(private _userRoleService: UserRoleService,
              private _sessionService: SessionService,
              private _toastService: ToastService,
              private _util: PpsUtilityService,
              private _spinner: NgxSpinnerService,
              private breakpointObserver: BreakpointObserver
  ) {
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });    
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.roleGridHeaders = this.getRoleGridHeader();
    this.loadRoles();

    this.projectColumnGridHeaders = this.getProjectColumnGridHeader();
  }

  onRoleGridReady(params) {
    this.roleGridApi = params.api;
  }

  onProjectColumnGridReady(params) {
    this.projectColumnGridApi = params.api;
  }

  onRoleSelectionChanged(params){
    var selectedRows = this.roleGridApi.getSelectedRows();
    this.selectedRole = selectedRows.length === 1 ? selectedRows[0] : null;
    this.loadProjectColumns(this.selectedRole.id);
  }

  loadRoles(){
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._userRoleService.getRoles()
      .subscribe(res => {
        if (res.succeed){
          this.roles = res.result;
        }else{
          this._toastService.failed("Load roles failed: " + res.errors.join(";"));
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  loadProjectColumns(roleId: number){
    this.loading = true;
    this.projectColumns = [];
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._userRoleService.getProjectColumnsAccessModelByRole(roleId)
      .subscribe(
        res => {
          if (res.succeed){
            this.projectColumns = res.result;
          }else{
            this._toastService.failed("Load project columns failed: " + res.errors.join(";"));
          }
        }, _ => { }, () => this.loading = false)
        .add(() => this._spinner.hide(this.spinnerName));
  }

  updateRoleProjectColumnAccessSetting(projectColumn: ProjectColumnAccessModel){
    this.subs.sink = this._userRoleService.updateProjectColumnAccessSetting(this.selectedRole.id, projectColumn)
      .subscribe(res => {
        if (res.succeed){
          this._sessionService.refreshUserContext();
          this._toastService.succeeded(`Access to [${projectColumn.columnDescription}] for role [${this.selectedRole.roleName}] updated successfully`);
        }else{
          this._toastService.failed(`Access to [${projectColumn.columnDescription}] for role [${this.selectedRole.roleName}] updated failed: ` + res.errors.join(";"));
        }
      });
  }

  private getRoleGridHeader(){
    var cols = [
      {
        headerName: 'Role',
        field:'roleName',
        filter: true,
        sortable: true,
        width: 180
      },
      {
        headerName: 'Total Users',
        field:'userCount',
        filter: false,
        sortable: true,
        width: 100,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => this._util.formatNumber(params.data[params.colDef.field], 0)
      },
      {
        headerName: 'Role Description',
        field:'roleDescription',
        filter: false,
        sortable: false,
        flex: 1
      }
    ];


    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }
    
    return cols;
  }

  private getProjectColumnGridHeader(){
    return [
      {
        headerName: 'Column',
        field:'columnDescription',
        filter: true,
        sortable: true,
        width: 260,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Access Setting',
        filter: false,
        sortable: false,
        flex: 1,
        cellRenderer: AccessSettingActionComponent,
        cellStyle: this.getCellStyleWithCenterAlign
      }
    ]
  }

  private getCellStyleWithCenterAlign(params){
    switch(params.data.accessPermission){
      case AccessPermission.NoAccess:
        return {textAlign:'center', backgroundColor: '#ffffff'};
      case AccessPermission.ReadAccess:
        return {textAlign:'center', backgroundColor: '#ffffaa'};
      case AccessPermission.WriteAccess:
        return {textAlign:'center', backgroundColor: '#44aa44', color: '#ffffff'};
      }
  }

  private getCellStyle(params){
    switch(params.data.accessPermission){
      case AccessPermission.NoAccess:
        return {backgroundColor: '#ffffff'};
      case AccessPermission.ReadAccess:
        return {backgroundColor: '#ffffaa'};
      case AccessPermission.WriteAccess:
        return {backgroundColor: '#44aa44', color: '#ffffff'};
      }
  }
}
